import { useEffect } from "react";
import { CALENDAR_LINK_QUERY_KEY } from "@/lib/calendar/calendar-link-store";
import { readCalendarLink } from "@/lib/calendar/calendar-link-store";
import { calendarWindow, syncShiftsToCalendar } from "@/lib/calendar/sync-shifts-to-calendar";
import { App } from "@/lib/capacitor/app";
import { DeviceCalendar } from "@/lib/capacitor/calendar";
import { useMe } from "@/lib/hooks/use-me";
import { queryClient } from "@/lib/providers/query";
import { fetchShiftsForCalendar } from "@/lib/query/shift/fetch-shifts-for-calendar";
import { logger } from "@/logger";

/** Vaker dan dit heeft geen zin: een planner verzet geen dienst twee keer in vijf minuten. */
const MIN_INTERVAL_MS = 5 * 60 * 1000;

let lastRunAt = 0;
let isRunning = false;

/**
 * Houdt de gekoppelde agenda bij zonder dat de medewerker ernaar hoeft te kijken.
 *
 * Bij het openen van de app en bij elke terugkeer naar de voorgrond. Dat is het moment waarop
 * iemand toch al kijkt, en het is precies wat de ICS-feed niet kan: die wordt door de agenda-app
 * zelf opgehaald, soms pas uren later.
 *
 * Stil op de achtergrond: dit is geen actie van de gebruiker, dus een mislukking krijgt een
 * logregel en geen melding. De knop "Nu bijwerken" in de instellingen is de zichtbare route.
 */
export function CalendarSyncListener() {
  const { me } = useMe();
  const employeeId = me?.employee?.id;

  useEffect(() => {
    if (!DeviceCalendar.isAvailable() || !employeeId) {
      return;
    }

    async function run() {
      if (isRunning || Date.now() - lastRunAt < MIN_INTERVAL_MS || !employeeId) {
        return;
      }

      isRunning = true;
      try {
        const link = await readCalendarLink();
        if (!link) {
          return;
        }

        // Zonder toegang zou elke afspraak falen; dan is stil niets doen beter dan een
        // agenda die half bijgewerkt raakt.
        if (!(await DeviceCalendar.hasAccess())) {
          return;
        }

        const shifts = await fetchShiftsForCalendar(employeeId, calendarWindow());
        await syncShiftsToCalendar({ shifts, link });
        lastRunAt = Date.now();
        await queryClient.invalidateQueries({ queryKey: CALENDAR_LINK_QUERY_KEY });
      } catch (error) {
        logger.error("Agenda bijwerken op de achtergrond mislukt", { error });
      } finally {
        isRunning = false;
      }
    }

    void run();

    const listener = App.addStateChangeListener(({ isActive }) => {
      if (isActive) {
        void run();
      }
    });

    return () => {
      void listener.then((handle) => handle.remove());
    };
  }, [employeeId]);

  return null;
}
